import axios from "axios";
import { normalizePercent } from "../lib/number";
import type {
  ServiceRoute,
  ServicePolicy,
  CreateRouteRequest,
  UpdateRouteRequest,
  CreatePolicyRequest,
  UpdatePolicyRequest,
  User,
  Team,
  Project,
  APIKey,
  NotificationEvent,
  NotificationSubscription,
  NotificationHealth,
  NotificationSystemStatus,
  NotificationStats,
  CreateNotificationSubscriptionRequest,
  UpdateNotificationSubscriptionRequest,
  Notification,
  DeliveryLog,
  EventType,
  CreateEventTypeRequest,
  UpdateEventTypeRequest,
  SubscriptionStats,
  UpdateSubscriptionRequest,
  Subscription,
  SSOOrigin,
} from "../types/admin";

// Use admin-specific base URL for admin management endpoints
const ADMIN_API_BASE_URL =
  import.meta.env.VITE_ADMIN_API_BASE_URL || "http://localhost:8000/v1/admin";

const api = axios.create({
  baseURL: ADMIN_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("auth_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Routes Management
export const adminService = {
  // Service Routes
  async listRoutes(enabledOnly = false): Promise<ServiceRoute[]> {
    const { data } = await api.get("/routes", {
      params: { enabled_only: enabledOnly },
    });
    return data;
  },

  async getRoute(routeId: string): Promise<ServiceRoute> {
    const { data } = await api.get(`/routes/${routeId}`);
    return data;
  },

  async createRoute(route: CreateRouteRequest): Promise<ServiceRoute> {
    const { data } = await api.post("/routes", route);
    return data;
  },

  async updateRoute(
    routeId: string,
    updates: UpdateRouteRequest,
  ): Promise<ServiceRoute> {
    const { data } = await api.patch(`/routes/${routeId}`, updates);
    return data;
  },

  async deleteRoute(routeId: string): Promise<void> {
    await api.delete(`/routes/${routeId}`);
  },

  async toggleRoute(routeId: string, enabled: boolean): Promise<ServiceRoute> {
    const { data } = await api.post(`/routes/${routeId}/toggle`, null, {
      params: { enabled },
    });
    return data;
  },

  // Service Policies
  async listPolicies(enabledOnly = false): Promise<ServicePolicy[]> {
    const { data } = await api.get("/policies", {
      params: { enabled_only: enabledOnly },
    });
    // Handle both formats: direct array or { policies: [...] }
    if (Array.isArray(data)) {
      return data;
    } else if (data && typeof data === "object" && "policies" in data) {
      return data.policies;
    }
    return [];
  },

  async getPolicy(policyId: string): Promise<ServicePolicy> {
    const { data } = await api.get(`/policies/${policyId}`);
    return data;
  },

  async getPoliciesByService(serviceName: string): Promise<ServicePolicy[]> {
    const { data } = await api.get(`/policies/service/${serviceName}`);
    return data.policies;
  },

  async createPolicy(policy: CreatePolicyRequest): Promise<ServicePolicy> {
    const { data } = await api.post("/policies", policy);
    return data;
  },

  async updatePolicy(
    policyId: string,
    updates: UpdatePolicyRequest,
  ): Promise<ServicePolicy> {
    const { data } = await api.put(`/policies/${policyId}`, updates);
    return data;
  },

  async deletePolicy(policyId: string): Promise<void> {
    await api.delete(`/policies/${policyId}`);
  },

  async togglePolicy(
    policyId: string,
    enabled: boolean,
  ): Promise<ServicePolicy> {
    const { data } = await api.post(`/policies/${policyId}/toggle`, null, {
      params: { enabled },
    });
    return data;
  },

  // Users Management
  async listUsers(params?: {
    org_id?: string;
    team_id?: string;
    project_id?: string;
    search?: string;
    sort_by?: string;
    sort_order?: string;
    cursor?: string;
    page_size?: number;
  }): Promise<{
    users: User[];
    total: number;
    has_more: boolean;
    next_cursor?: string;
  }> {
    const { data } = await api.get("/users", {
      params: params || {},
    });
    // Backend now returns paginated response
    return data;
  },

  async getUser(userId: string): Promise<User> {
    const { data } = await api.get(`/users/${userId}`);
    return data;
  },

  async createUser(user: {
    user_email: string;
    user_name: string;
    org_id: string;
    auth_provider?: string;
  }): Promise<User> {
    const { data } = await api.post("/users", user);
    return data;
  },

  async updateUser(userId: string, updates: Partial<User>): Promise<User> {
    const { data } = await api.patch(`/users/${userId}`, updates);
    return data;
  },

  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/users/${userId}`);
  },

  // User Memberships Management
  async getUserMemberships(userId: string): Promise<{
    user_id: string;
    teams: Array<{
      team_id: string;
      team_name: string;
      role: string;
      org_id: string;
      assigned_at?: string;
    }>;
    projects: Array<{
      project_id: string;
      project_name: string;
      team_id: string;
      role: string;
      assigned_at?: string;
    }>;
  }> {
    const { data } = await api.get(`/users/${userId}/memberships`);
    return data;
  },

  async removeUserFromTeam(teamId: string, userId: string): Promise<void> {
    await api.delete(`/teams/${teamId}/members/${userId}`);
  },

  async removeUserFromProject(
    projectId: string,
    userId: string,
  ): Promise<void> {
    await api.delete(`/projects/${projectId}/members/${userId}`);
  },

  async addUserToTeam(
    teamId: string,
    userId: string,
    role: string = "member",
  ): Promise<void> {
    await api.post(`/teams/${teamId}/members`, { user_id: userId, role });
  },

  async addUserToProject(
    projectId: string,
    userId: string,
    role: string = "viewer",
  ): Promise<void> {
    await api.post(`/projects/${projectId}/members`, { user_id: userId, role });
  },

  async inviteUser(invitation: {
    user_email: string;
    role: string;
    team_id?: string;
    project_id?: string;
  }): Promise<User> {
    const { data } = await api.post("/users/invite", invitation);
    return data;
  },

  // Teams Management
  async listTeams(orgId?: string): Promise<Team[]> {
    const { data } = await api.get("/teams", {
      params: { org_id: orgId },
    });
    return data;
  },

  async getTeam(teamId: string): Promise<Team> {
    const { data } = await api.get(`/teams/${teamId}`);
    return data;
  },

  async createTeam(team: {
    team_name: string;
    description?: string;
    settings?: Record<string, any>;
  }): Promise<Team> {
    const { data } = await api.post("/teams", team);
    return data;
  },

  async updateTeam(
    teamId: string,
    updates: {
      team_name?: string;
      description?: string;
      settings?: Record<string, any>;
    },
  ): Promise<Team> {
    const { data } = await api.put(`/teams/${teamId}`, updates);
    return data;
  },

  async deleteTeam(teamId: string): Promise<void> {
    await api.delete(`/teams/${teamId}`);
  },

  async updateTeamStatus(
    teamId: string,
    status: "active" | "inactive",
  ): Promise<Team> {
    const { data } = await api.patch(`/teams/${teamId}/status`, null, {
      params: { status },
    });
    return data;
  },

  async listTeamMembers(teamId: string): Promise<any[]> {
    const { data } = await api.get(`/teams/${teamId}/members`);
    return data.members;
  },

  async addTeamMember(
    teamId: string,
    userId: string,
    role: string,
  ): Promise<void> {
    await api.post(`/teams/${teamId}/members`, { user_id: userId, role });
  },

  // Team Policy Management
  async getTeamPolicy(teamId: string): Promise<any> {
    const { data } = await api.get(`/teams/${teamId}/policy`);
    return data;
  },

  async createTeamPolicy(
    teamId: string,
    policy: {
      allowed_services: string[];
      rate_limit?: number;
      quota_multiplier?: number;
      description?: string;
      enabled?: boolean;
    },
  ): Promise<any> {
    const { data } = await api.post(`/teams/${teamId}/policy`, policy);
    return data;
  },

  async updateTeamPolicy(
    teamId: string,
    policy: {
      allowed_services?: string[];
      rate_limit?: number;
      quota_multiplier?: number;
      description?: string;
      enabled?: boolean;
    },
  ): Promise<any> {
    const { data } = await api.patch(`/teams/${teamId}/policy`, policy);
    return data;
  },

  async deleteTeamPolicy(teamId: string): Promise<void> {
    await api.delete(`/teams/${teamId}/policy`);
  },

  async listAvailableServices(): Promise<string[]> {
    const { data } = await api.get("/teams/available-services");
    return data;
  },

  async removeTeamMember(teamId: string, userId: string): Promise<void> {
    await api.delete(`/teams/${teamId}/members/${userId}`);
  },

  async bulkAddTeamMembers(
    teamId: string,
    userIds: string[],
    role: string = "member",
  ): Promise<{
    total: number;
    succeeded: number;
    failed: number;
    results: Array<{ user_id: string; success: boolean; error?: string }>;
  }> {
    const { data } = await api.post(`/teams/${teamId}/members/bulk`, {
      user_ids: userIds,
      role,
    });
    return data;
  },

  // Projects Management
  async listProjects(teamId?: string): Promise<Project[]> {
    const { data } = await api.get("/projects", {
      params: { team_id: teamId },
    });
    return data;
  },

  async getProject(projectId: string): Promise<Project> {
    const { data } = await api.get(`/projects/${projectId}`);
    return data;
  },

  async createProject(project: {
    project_name: string;
    team_id: string;
    description?: string;
    allowed_services?: string[];
    rate_limit?: number;
  }): Promise<Project> {
    const { data } = await api.post("/projects", project);
    return data;
  },

  async updateProject(
    projectId: string,
    updates: {
      project_name?: string;
      description?: string;
      allowed_services?: string[];
      rate_limit?: number;
      enable_audit_log?: boolean;
    },
  ): Promise<Project> {
    const { data } = await api.put(`/projects/${projectId}`, updates);
    return data;
  },

  async deleteProject(projectId: string): Promise<void> {
    await api.delete(`/projects/${projectId}`);
  },

  // API Keys Management
  async listAPIKeys(
    projectId?: string,
    includeRevoked: boolean = false,
  ): Promise<APIKey[]> {
    const { data } = await api.get("/api-keys", {
      params: {
        project_id: projectId,
        include_revoked: includeRevoked,
      },
    });
    return data; // Backend returns direct array, not wrapped in object
  },

  async createAPIKey(apiKey: {
    project_id: string;
    name: string;
    expires_in_days?: number;
  }): Promise<{
    api_key: string;
    key_id: string;
    project_id: string;
    name: string;
    scopes: string[];
    warning?: string;
  }> {
    const { data } = await api.post("/api-keys", apiKey);
    return data;
  },

  async revokeAPIKey(keyId: string): Promise<void> {
    await api.delete(`/api-keys/${keyId}`);
  },

  async rotateAPIKey(keyId: string): Promise<{
    api_key: string;
    key_id: string;
    project_id: string;
    name: string;
    scopes: string[];
    warning?: string;
  }> {
    const { data } = await api.post(`/api-keys/${keyId}/rotate`);
    return data;
  },

  async getAPIKey(keyId: string): Promise<APIKey> {
    const { data } = await api.get(`/api-keys/${keyId}`);
    return data;
  },

  // Project Metadata Management
  async getProjectMetadata(
    projectId: string,
    key?: string,
  ): Promise<Record<string, any>> {
    const params = key ? { key } : {};
    const { data } = await api.get(`/projects/${projectId}/metadata`, {
      params,
    });
    return data;
  },

  async setProjectMetadata(
    projectId: string,
    key: string,
    value: any,
  ): Promise<Project> {
    const { data } = await api.put(`/projects/${projectId}/metadata`, {
      key,
      value,
    });
    return data;
  },

  async deleteProjectMetadata(
    projectId: string,
    key: string,
  ): Promise<Project> {
    const { data } = await api.delete(`/projects/${projectId}/metadata/${key}`);
    return data;
  },

  // Project Member Management
  async listProjectMembers(projectId: string): Promise<any[]> {
    const { data } = await api.get(`/projects/${projectId}/members`);
    return data;
  },

  async addProjectMember(
    projectId: string,
    memberData: {
      user_id?: string;
      user_email?: string;
      role: string;
    },
  ): Promise<{ message: string }> {
    const { data } = await api.post(
      `/projects/${projectId}/members`,
      memberData,
    );
    return data;
  },

  async removeProjectMember(projectId: string, userId: string): Promise<void> {
    await api.delete(`/projects/${projectId}/members/${userId}`);
  },

  async bulkAddProjectMembers(
    projectId: string,
    userIds: string[],
    role: string = "viewer",
  ): Promise<{
    total: number;
    succeeded: number;
    failed: number;
    results: Array<{ user_id: string; success: boolean; error?: string }>;
  }> {
    const { data } = await api.post(`/projects/${projectId}/members/bulk`, {
      user_ids: userIds,
      role,
    });
    return data;
  },

  // SSO Origins Management
  async listSSOOrigins(params?: {
    org_id?: string;
    include_global?: boolean;
    active_only?: boolean;
  }): Promise<{ origins: SSOOrigin[]; total: number }> {
    const { data } = await api.get("/sso-origins", { params });
    return data;
  },

  async getSSOOrigin(originId: string): Promise<SSOOrigin> {
    const { data } = await api.get(`/sso-origins/${originId}`);
    return data;
  },

  async createSSOOrigin(origin: {
    origin_pattern: string;
    pattern_type: "exact" | "wildcard";
    org_id?: string | null;
    description?: string;
    is_active?: boolean;
  }): Promise<SSOOrigin> {
    const { data } = await api.post("/sso-origins", origin);
    return data;
  },

  async updateSSOOrigin(
    originId: string,
    updates: {
      origin_pattern?: string;
      pattern_type?: "exact" | "wildcard";
      description?: string;
      is_active?: boolean;
    },
  ): Promise<SSOOrigin> {
    const { data } = await api.put(`/sso-origins/${originId}`, updates);
    return data;
  },

  async deleteSSOOrigin(originId: string): Promise<void> {
    await api.delete(`/sso-origins/${originId}`);
  },

  async toggleSSOOrigin(
    originId: string,
    isActive: boolean,
  ): Promise<SSOOrigin> {
    const { data } = await api.post(`/sso-origins/${originId}/toggle`, null, {
      params: { is_active: isActive },
    });
    return data;
  },

  // Notification Service Management
  // Note: These methods call through the Atlas Guard proxy for security and centralized auth
  notificationService: {
    // Use Atlas Guard proxy instead of direct service URL
    // Must match the service_name in database: notification_service
    baseURL: `${ADMIN_API_BASE_URL.replace("/admin", "")}/notification_service`,

    // Create axios instance that uses the proxy
    createApi() {
      const token = localStorage.getItem("auth_token");
      return axios.create({
        baseURL: this.baseURL,
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
      });
    },

    // Health & Monitoring
    async getHealth(): Promise<NotificationHealth> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.get("/health");
      return data;
    },

    async getDetailedHealth(): Promise<NotificationHealth> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.get("/health/detailed");
      return data;
    },

    async getSystemStatus(): Promise<NotificationSystemStatus> {
      try {
        const notificationApi = this.createApi();
        // Get multiple system status endpoints
        const [deliveryServices, pubsubStatus, circuitBreakers] =
          await Promise.all([
            notificationApi
              .get("/admin/system/delivery-services/status")
              .catch(() => ({ data: null })),
            notificationApi
              .get("/admin/system/pubsub/status")
              .catch(() => ({ data: null })),
            notificationApi
              .get("/admin/system/circuit-breakers/status")
              .catch(() => ({ data: null })),
          ]);

        // Transform to NotificationSystemStatus structure
        return {
          pubsub_processor: {
            status: pubsubStatus.data?.is_running ? "running" : "stopped",
            message_count: pubsubStatus.data?.total_processed || 0,
            processing_rate: 0,
            errors: pubsubStatus.data?.error_count || 0,
          },
          delivery_services: deliveryServices.data || {
            email: { enabled: true, status: "healthy", success_rate: 0 },
            webhook: { enabled: true, status: "healthy", success_rate: 0 },
            firebase: { enabled: true, status: "healthy", success_rate: 0 },
          },
          circuit_breaker: circuitBreakers.data || {
            email: "closed",
            webhook: "closed",
            firebase: "closed",
          },
        };
      } catch (error) {
        console.warn("System status endpoints not fully available:", error);
        // Return default structure
        return {
          pubsub_processor: {
            status: "stopped",
            message_count: 0,
            processing_rate: 0,
            errors: 0,
          },
          delivery_services: {
            email: { enabled: true, status: "healthy", success_rate: 0 },
            webhook: { enabled: true, status: "healthy", success_rate: 0 },
            firebase: { enabled: true, status: "healthy", success_rate: 0 },
          },
          circuit_breaker: {
            email: "closed",
            webhook: "closed",
            firebase: "closed",
          },
        };
      }
    },

    async getSystemStats(): Promise<NotificationStats> {
      const notificationApi = this.createApi();
      // Use the admin stats endpoint
      const { data } = await notificationApi.get(
        "/admin/notifications/stats/summary",
      );

      const successRate = normalizePercent(data?.success_rate);
      const failureRate = normalizePercent(data?.failure_rate);

      // Transform the actual response to match NotificationStats interface
      return {
        total_notifications: data.total_notifications || 0,
        by_status: data.by_status || {},
        by_delivery_channel: data.by_delivery_channel || {},
        success_rate: successRate,
        failure_rate: failureRate,
        average_delivery_time_ms: data.average_delivery_time_ms || 0,
        timestamp: data.timestamp || new Date().toISOString(),
        // Computed fields for UI
        total_sent: data.total_notifications || 0,
        total_delivered:
          data.by_status?.delivered || data.by_status?.success || 0,
        total_failed: data.by_status?.failed || 0,
        avg_delivery_time: data.average_delivery_time_ms || 0,
        events_by_type: data.by_status || {},
        delivery_success_rate: {
          email: 95, // Default values since not in response (percent)
          webhook: 98,
          firebase: 92,
          overall: successRate,
        },
        recent_activity: data.recent_activity || {},
      };
    },

    async getMetrics(): Promise<Record<string, any>> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.get("/health/metrics");
      return data;
    },

    // System Management endpoints
    async getPubSubStatus(): Promise<any> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.get("/admin/system/pubsub/status");
      return data;
    },

    async getCircuitBreakersStatus(): Promise<any> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.get(
        "/admin/system/circuit-breakers/status",
      );
      return data;
    },

    async getServicesStatus(): Promise<any> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.get(
        "/admin/system/services/status",
      );
      return data;
    },

    async getRecentEvents(params?: { limit?: number }): Promise<any> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.get(
        "/admin/system/events/recent",
        { params },
      );
      return data;
    },

    async getCurrentConfig(): Promise<any> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.get(
        "/admin/system/config/current",
      );
      return data;
    },

    // Additional System Management endpoints
    async getSystemHealthDetailed(): Promise<any> {
      try {
        const notificationApi = this.createApi();
        const { data } = await notificationApi.get(
          "/admin/system/health/detailed",
        );
        return data;
      } catch (error) {
        console.error("System health endpoint failed:", error);
        throw error;
      }
    },

    async getBackgroundServicesStatus(): Promise<any> {
      try {
        const notificationApi = this.createApi();
        const { data } = await notificationApi.get(
          "/admin/system/services/status",
        );
        return data;
      } catch (error) {
        console.error("Services status endpoint failed:", error);
        return null;
      }
    },

    async getSystemStatsOverview(): Promise<any> {
      try {
        const notificationApi = this.createApi();
        const { data } = await notificationApi.get(
          "/admin/system/stats/overview",
        );
        return data;
      } catch (error) {
        console.error("Stats overview endpoint failed:", error);
        return {
          total_notifications: 0,
          by_status: {},
          by_delivery_channel: {},
          success_rate: 0,
          failure_rate: 0,
          average_delivery_time_ms: 0,
          timestamp: new Date().toISOString(),
          total_sent: 0,
          total_delivered: 0,
          total_failed: 0,
          avg_delivery_time: 0,
        };
      }
    },

    async getDeliveryServicesStatus(): Promise<any> {
      try {
        const notificationApi = this.createApi();
        const { data } = await notificationApi.get(
          "/admin/system/delivery-services/status",
        );
        return data;
      } catch (error) {
        console.error("Delivery services endpoint failed:", error);
        return {};
      }
    },

    async startBackgroundServices(): Promise<any> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.post(
        "/admin/system/services/start",
      );
      return data;
    },

    async stopBackgroundServices(): Promise<any> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.post(
        "/admin/system/services/stop",
      );
      return data;
    },

    async cleanupSystem(): Promise<any> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.post(
        "/admin/system/maintenance/cleanup",
      );
      return data;
    },

    // Event Publishing
    async publishEvent(
      event: NotificationEvent,
    ): Promise<{ message: string; correlation_id: string }> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.post("/events/publish", event, {
        headers: { "X-Correlation-ID": `req_${Date.now()}` },
      });
      return data;
    },

    // Subscription Management
    async listSubscriptions(params?: {
      event_type?: string;
      user_id?: string;
      delivery_channel?: string;
      is_active?: boolean;
    }): Promise<NotificationSubscription[]> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.get("/subscriptions", { params });
      return data;
    },

    async getSubscription(
      subscriptionId: string,
    ): Promise<NotificationSubscription> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.get(
        `/subscriptions/${subscriptionId}`,
      );
      return data;
    },

    async createSubscription(
      subscription: CreateNotificationSubscriptionRequest,
    ): Promise<NotificationSubscription> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.post(
        "/subscriptions",
        subscription,
      );
      return data;
    },

    async updateSubscription(
      subscriptionId: string,
      updates: UpdateNotificationSubscriptionRequest,
    ): Promise<NotificationSubscription> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.put(
        `/subscriptions/${subscriptionId}`,
        updates,
      );
      return data;
    },

    async deleteSubscription(subscriptionId: string): Promise<void> {
      const notificationApi = this.createApi();
      await notificationApi.delete(`/subscriptions/${subscriptionId}`);
    },

    async toggleSubscription(
      subscriptionId: string,
      isActive: boolean,
    ): Promise<NotificationSubscription> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.patch(
        `/subscriptions/${subscriptionId}`,
        {
          is_active: isActive,
        },
      );
      return data;
    },

    async pauseSubscription(
      subscriptionId: string,
      isPaused: boolean,
    ): Promise<NotificationSubscription> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.patch(
        `/subscriptions/${subscriptionId}`,
        {
          is_paused: isPaused,
        },
      );
      return data;
    },

    // Failed Notifications & Retry (Phase 1) - Using real notification service
    async listNotifications(params?: {
      status?: string;
      delivery_channel?: string;
      skip?: number;
      limit?: number;
    }): Promise<Notification[]> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.get("/admin/notifications/", {
        params,
      });
      const notifications = data?.notifications || [];

      // Normalize notification-service responses to our UI shape.
      // The notification service may return fields like:
      // - last_error instead of error_message
      // - subject like "Notification: <event_type>" instead of event_type
      // - failed_at/scheduled_at instead of created_at
      return notifications.map((n: any) => {
        const subject: string | undefined = n?.subject;
        const subjectEventType =
          typeof subject === "string" && subject.startsWith("Notification:")
            ? subject.replace(/^Notification:\s*/, "").trim()
            : undefined;

        return {
          id: n?.id,
          event_id: n?.event_id,
          subscription_id: n?.subscription_id,
          user_id: n?.user_id,
          event_type: n?.event_type || subjectEventType,
          delivery_channel: n?.delivery_channel,
          status: n?.status,
          error_message: n?.error_message || n?.last_error,
          // Use most relevant timestamp for "Failed At" column
          created_at: n?.failed_at || n?.created_at || n?.scheduled_at,
          delivered_at: n?.delivered_at || n?.sent_at,
          payload: n?.payload,
        } satisfies Notification;
      });
    },

    async getNotification(notificationId: string): Promise<Notification> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.get(
        `/admin/notifications/${notificationId}`,
      );
      return data;
    },

    async retryNotification(
      notificationId: string,
      config?: {
        max_attempts?: number;
        delay_seconds?: number;
      },
    ): Promise<void> {
      const notificationApi = this.createApi();
      await notificationApi.post(
        `/admin/notifications/${notificationId}/retry`,
        config,
      );
    },

    // Delivery Logs (Phase 2) - Using real notification service
    async getDeliveryLogs(params?: {
      hours?: number;
      limit?: number;
      status?: string;
      provider?: string;
    }): Promise<DeliveryLog[]> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.get(
        "/admin/notifications/delivery-logs/recent",
        { params },
      );
      // Handle both array and object responses
      const logs = Array.isArray(data)
        ? data
        : data.logs || data.delivery_logs || [];

      // Normalize delivery log response. The notification service often returns:
      // - timestamp / attempted_at instead of created_at
      // - latency_ms instead of duration_ms
      // - error_message/provider_response instead of response_message
      return logs.map((l: any) => {
        const status =
          l?.status === "sent" ? "success" : l?.status || "unknown";

        return {
          id: l?.id,
          notification_id: l?.notification_id,
          provider: l?.provider || l?.delivery_channel || "unknown",
          status,
          response_message:
            l?.response_message ||
            l?.error_message ||
            (l?.provider_response
              ? JSON.stringify(l.provider_response)
              : undefined),
          duration_ms:
            l?.duration_ms ??
            l?.latency_ms ??
            l?.provider_response?.response_time_ms ??
            0,
          created_at: l?.created_at || l?.attempted_at || l?.timestamp,
        } satisfies DeliveryLog;
      });
    },

    async getNotificationLogs(notificationId: string): Promise<DeliveryLog[]> {
      const notificationApi = this.createApi();
      const { data } = await notificationApi.get(
        `/admin/notifications/${notificationId}/logs`,
      );
      // Handle both array and object responses
      return Array.isArray(data) ? data : data.logs || data.delivery_logs || [];
    },

    // Event Types Management (Phase 3) - Using real notification service
    async listEventTypes(params?: {
      active_only?: boolean;
      category?: string;
    }): Promise<EventType[]> {
      try {
        const notificationApi = this.createApi();
        const { data } = await notificationApi.get("/admin/event-types/", {
          params,
        });
        // Handle both array and object responses
        return Array.isArray(data) ? data : data.event_types || [];
      } catch (error) {
        console.warn("Event types endpoint not available:", error);
        // Return empty array if endpoint fails
        return [];
      }
    },

    async createEventType(
      eventType: CreateEventTypeRequest,
    ): Promise<EventType> {
      try {
        const notificationApi = this.createApi();
        const { data } = await notificationApi.post(
          "/admin/event-types/",
          eventType,
        );
        return data;
      } catch (error) {
        console.warn("Create event type endpoint not available:", error);
        throw new Error("Event type creation not available");
      }
    },

    async updateEventType(
      eventTypeId: string,
      updates: UpdateEventTypeRequest,
    ): Promise<EventType> {
      try {
        const notificationApi = this.createApi();
        const { data } = await notificationApi.put(
          `/admin/event-types/${eventTypeId}`,
          updates,
        );
        return data;
      } catch (error) {
        console.warn("Update event type endpoint not available:", error);
        throw new Error("Event type update not available");
      }
    },

    async deleteEventType(eventTypeId: string): Promise<void> {
      try {
        const notificationApi = this.createApi();
        await notificationApi.delete(`/admin/event-types/${eventTypeId}`);
      } catch (error) {
        console.warn("Delete event type endpoint not available:", error);
        throw new Error("Event type deletion not available");
      }
    },

    async getEventTypeCategories(): Promise<string[]> {
      try {
        const notificationApi = this.createApi();
        const { data } = await notificationApi.get(
          "/admin/event-types/categories/list",
        );
        return data;
      } catch (error) {
        console.warn("Event type categories endpoint not available:", error);
        return [];
      }
    },

    // Subscriptions Management (Phase 4) - Using real notification service
    async listAllSubscriptions(params?: {
      active_only?: boolean;
      delivery_channel?: string;
      event_type?: string;
    }): Promise<Subscription[]> {
      try {
        const notificationApi = this.createApi();
        const { data } = await notificationApi.get("/admin/subscriptions/", {
          params,
        });
        // Handle both array and object responses
        return Array.isArray(data) ? data : data.subscriptions || [];
      } catch (error) {
        console.warn("Subscriptions endpoint not available:", error);
        return [];
      }
    },

    async getSubscriptionById(subscriptionId: string): Promise<Subscription> {
      try {
        const notificationApi = this.createApi();
        const { data } = await notificationApi.get(
          `/admin/subscriptions/${subscriptionId}`,
        );
        return data;
      } catch (error) {
        console.warn("Get subscription endpoint not available:", error);
        throw new Error("Subscription not available");
      }
    },

    async updateSubscriptionById(
      subscriptionId: string,
      updates: UpdateSubscriptionRequest,
    ): Promise<Subscription> {
      try {
        const notificationApi = this.createApi();
        const { data } = await notificationApi.put(
          `/admin/subscriptions/${subscriptionId}`,
          updates,
        );
        return data;
      } catch (error) {
        console.warn("Update subscription endpoint not available:", error);
        throw new Error("Subscription update not available");
      }
    },

    async deleteSubscriptionById(subscriptionId: string): Promise<void> {
      try {
        const notificationApi = this.createApi();
        await notificationApi.delete(`/admin/subscriptions/${subscriptionId}`);
      } catch (error) {
        console.warn("Delete subscription endpoint not available:", error);
        throw new Error("Subscription deletion not available");
      }
    },

    async getSubscriptionStats(): Promise<SubscriptionStats> {
      try {
        const notificationApi = this.createApi();
        const { data } = await notificationApi.get(
          "/admin/subscriptions/stats/summary",
        );
        return data;
      } catch (error) {
        console.warn("Subscription stats endpoint not available:", error);
        return {
          total_subscriptions: 0,
          active_subscriptions: 0,
          by_channel: {},
          by_event_type: {},
        };
      }
    },
  },
};

export default adminService;
